// Every website the signed-in person can open, one record each, whatever made it (bloxx-data GET /v1/websites,
// src/identity/websites.ts). The Sites list, /sites/:ref and the Content performance picker read these hooks, so
// a website started in the builder or through Claude shows up everywhere the moment it exists.
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { tokenStore } from "./authTransport";
import type { WebsiteBusinessProfile, WebsiteList, WebsiteOne, WebsiteRecord } from "@wire";

export type { WebsiteRecord } from "@wire";

const BASE = () => (import.meta.env.VITE_BLOXX_DATA_URL as string | undefined) || "https://data.bloxx.page";

export class WebsiteError extends Error {
  constructor(message: string, readonly status: number, readonly code: string | null, readonly retryable: boolean) {
    super(message);
  }
}

async function request<T>(path: string, init: { method?: string; signal?: AbortSignal } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE() + path, {
      method: init.method ?? "GET",
      signal: init.signal,
      headers: { authorization: `Bearer ${tokenStore.get() ?? ""}`, accept: "application/json" },
    });
  } catch (error) {
    if (init.signal?.aborted) throw error;
    throw new WebsiteError("Your websites could not be loaded. Check your connection and try again.", 0, null, true);
  }
  const body = (await res.json().catch(() => null)) as (T & { message?: string; error?: string; code?: string; retryable?: boolean }) | null;
  if (!res.ok) {
    throw new WebsiteError(body?.message || body?.error || "Your websites could not be loaded. Try again in a minute.", res.status, body?.code ?? null, body?.retryable ?? res.status >= 500);
  }
  return body as T;
}

export const websitesKey = ["websites"] as const;

/** Every page of the list (200 a page), in the server's order. */
export async function listWebsites(signal?: AbortSignal): Promise<WebsiteList> {
  const websites: WebsiteRecord[] = [];
  let cursor: string | null = null;
  let checks: WebsiteList["checks"] | null = null;
  for (let page = 0; page < 10; page++) {
    const answer: WebsiteList = await request<WebsiteList>(`/v1/websites?limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`, { signal });
    websites.push(...answer.websites);
    checks ??= answer.checks;
    cursor = answer.next_cursor;
    if (!cursor) break;
  }
  rememberJourneys(websites.filter((w) => w.journey_id).map((w) => ({ w })));
  return { websites, next_cursor: null, checks: checks! };
}

export function useWebsites() {
  return useQuery({ queryKey: websitesKey, queryFn: ({ signal }) => listWebsites(signal), staleTime: 30_000, retry: 1 });
}

/** One website by any ref (Xano id, slug, journey id, hostname). Seeded from the list when it is already loaded. */
export function useWebsite(ref: string | undefined) {
  const qc = useQueryClient();
  return useQuery<WebsiteOne, Error>({
    queryKey: ["website", ref],
    enabled: !!ref,
    retry: (count, error) => error instanceof WebsiteError && error.retryable && count < 1,
    queryFn: async ({ signal }) => {
      try {
        const one = await request<WebsiteOne>(`/v1/websites/${encodeURIComponent(ref!)}`, { signal });
        rememberJourney(one.website, ref);
        return one;
      } catch (error) {
        // A website this person can no longer open must not keep opening its editor from a remembered journey.
        if (error instanceof WebsiteError && !error.retryable && (error.status === 403 || error.status === 404)) forgetJourney(ref!);
        throw error;
      }
    },
    placeholderData: (): WebsiteOne | undefined => {
      const listed = qc.getQueryData<WebsiteList>(websitesKey)?.websites.find((w) => w.ref === ref || String(w.xano_site_id) === ref || w.journey_id === ref);
      return listed ? { website: listed, checks: qc.getQueryData<WebsiteList>(websitesKey)!.checks } : undefined;
    },
  });
}

/* ───────────────────────── which journey a ref names ───────────────────────── */
//
// The full-screen editor reads everything by journey id, but its address carries the website's ref. Asking
// /v1/websites/<ref> first cost a whole round trip (3 to 6 s cold) before any of the editor's own reads could start.
// The answer is remembered here: in the query cache (the website itself, or the list) and, for a reload or a new tab
// in this session, in sessionStorage. Storage is a convenience only: blocked, full or private, the editor just asks.
// The journey id is a key, not a permission: workspace/me and every workspace read still check the caller.

const JOURNEYS_KEY = "bloxx:site-journeys";
const JOURNEYS_MAX = 40;
const JOURNEY_ID = /^journey-[a-z0-9]{8,64}$/;
type Remembered = Record<string, { j: string; at: number }>;

function readJourneys(): Remembered {
  try {
    const saved = JSON.parse(sessionStorage.getItem(JOURNEYS_KEY) ?? "{}") as unknown;
    return saved && typeof saved === "object" && !Array.isArray(saved) ? (saved as Remembered) : {};
  } catch {
    return {};
  }
}
function writeJourneys(map: Remembered) {
  try {
    const newest = Object.entries(map).sort(([, a], [, b]) => (b?.at ?? 0) - (a?.at ?? 0)).slice(0, JOURNEYS_MAX);
    sessionStorage.setItem(JOURNEYS_KEY, JSON.stringify(Object.fromEntries(newest)));
  } catch {
    // Without storage the next open asks /v1/websites, as before.
  }
}

/** Remember the journey each website's ref (and the ref it was asked by, when different) names. A website with none is forgotten. */
export function rememberJourneys(list: { w: Pick<WebsiteRecord, "ref" | "journey_id">; asked?: string }[]) {
  if (!list.length) return;
  const map = readJourneys();
  const at = Date.now();
  for (const { w, asked } of list) {
    for (const ref of new Set([w.ref, asked].filter((r): r is string => !!r))) {
      if (w.journey_id && JOURNEY_ID.test(w.journey_id)) map[ref] = { j: w.journey_id, at };
      else delete map[ref];
    }
  }
  writeJourneys(map);
}
export const rememberJourney = (w: Pick<WebsiteRecord, "ref" | "journey_id">, asked?: string) => rememberJourneys([{ w, asked }]);

export function forgetJourney(ref: string) {
  const map = readJourneys();
  if (!map[ref]) return;
  delete map[ref];
  writeJourneys(map);
}

/**
 * The journey id a ref names, if this browser already knows it: the website's own query, the list, this session's
 * memory, or the ref itself when it is a journey id. null when only /v1/websites can say.
 */
export function knownJourney(qc: Pick<QueryClient, "getQueryData">, ref: string | undefined): string | null {
  if (!ref) return null;
  const own = qc.getQueryData<WebsiteOne>(["website", ref])?.website;
  if (own) return own.journey_id && JOURNEY_ID.test(own.journey_id) ? own.journey_id : null;
  const listed = qc.getQueryData<WebsiteList>(websitesKey)?.websites.find((w) => w.ref === ref || w.journey_id === ref);
  if (listed) return listed.journey_id && JOURNEY_ID.test(listed.journey_id) ? listed.journey_id : null;
  const saved = readJourneys()[ref]?.j;
  if (typeof saved === "string" && JOURNEY_ID.test(saved)) return saved;
  return JOURNEY_ID.test(ref) ? ref : null;
}

/**
 * Does this website have a Xano site of its own that serves (pages, leads, Search Console, the Xano settings)? A
 * built website's own Xano row (WS2 step 8) that served nothing does not: the resolver gives it no `pages`.
 */
export const servedByXano = (w: Pick<WebsiteRecord, "xano_site_id" | "capabilities">) => w.xano_site_id != null && w.capabilities.includes("pages");

/**
 * Can Bloxx make this website a `website-<xano id>` business profile? Only for a Xano site. A website built in Bloxx
 * gets its profile while it is built (its journey is bound to it), even now that it carries its own Xano row
 * (WS2 step 8): asking for `website-<row>` would give one business a second profile.
 */
export const makesSiteProfile = (w: Pick<WebsiteRecord, "xano_site_id" | "journey_id">) => w.xano_site_id != null && !w.journey_id;

/**
 * The business profile a measured website never had (`website-<xano id>`), made on first use. Safe to call twice.
 * Asked of the MCP directly (POST /v1/sites/{id}/business-profile, WS2 step 11b), which checks the caller's role in
 * Xano; a website built in Bloxx gets its profile while it is built.
 */
export async function ensureWebsiteBusinessProfile(website: Pick<WebsiteRecord, "xano_site_id" | "journey_id">): Promise<Pick<WebsiteBusinessProfile, "business_profile">> {
  if (!makesSiteProfile(website)) throw new Error("A website made in Bloxx gets its business details while it is built. Open the website and finish its business details there.");
  try {
    // Loaded on demand: the shell never imports the Xano client eagerly.
    const { requestAppApi } = await import("./xano");
    const made = (await requestAppApi("POST", `/v1/sites/${website.xano_site_id}/business-profile`)) as { profile_id: string; created: boolean };
    return { business_profile: { id: made.profile_id, created: made.created === true } };
  } catch (e) {
    const sentence = (e as { payload?: { message?: unknown } }).payload?.message;
    throw new Error(typeof sentence === "string" && sentence ? sentence : "That website could not be set up. Try again.");
  }
}

/** Where a website's page lives. */
export const websitePath = (w: Pick<WebsiteRecord, "ref">) => `/sites/${encodeURIComponent(w.ref)}`;

export const statusTone = (status: WebsiteRecord["status"]): "success" | "warning" | "secondary" | "outline" =>
  status === "live" ? "success" : status === "attention" ? "warning" : status === "ready" ? "outline" : "secondary";

/** The websites opened most recently in this browser, newest first (the sidebar's Recent sites). Never required. */
const RECENT_KEY = "bloxx:recent-sites";
export function recentSites(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(saved) ? saved.filter((v): v is string => typeof v === "string").slice(0, 5) : [];
  } catch {
    return [];
  }
}
export function rememberSite(ref: string) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([ref, ...recentSites().filter((r) => r !== ref)].slice(0, 5)));
  } catch {
    // Without storage the list just starts from the top of Sites.
  }
}
